import React, { ReactNode, useEffect, useState } from 'react';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import FormField from '../../components/common/FormField';
import { EnquiryDTO } from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import { useFormValidation, ValidationRules } from '../../hooks/useFormValidation';
import { useEnquiryTypeApi } from '../../hooks/useEnquiryTypeApi';

interface EnquiryFormProps {
  initialValues: EnquiryDTO;
  onSubmit: (data: EnquiryDTO) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const FORM_SECTIONS = {
  basicInfo: {
    id: 'basic-info',
    title: 'Basic Information'
  }
} as const;

const EnquiryForm: React.FC<EnquiryFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  isLoading = false
}) => {
  const [formData, setFormData] = useState<EnquiryDTO>(initialValues);

  // Define the type for dropdown options
  type DropdownOption = { value: string; label: ReactNode };
  const [enquiryTypeOptions, setEnquiryTypeOptions] = useState<DropdownOption[]>([]);
  const { search: searchEnquiryTypes, isLoading: isLoadingEnquiryTypes } = useEnquiryTypeApi();

  useEffect(() => {
    const loadDropdownData = async () => {
      try {
        const enquiryTypes = await searchEnquiryTypes({
          page: 1,
          pageSize: 999,
          sortBy: 'displayPosition'
        });

        setEnquiryTypeOptions(
          enquiryTypes?.data?.results?.map((et) => ({
            value: et.id ?? '',
            label: et.title ?? '',
          })) || []
        );
      } catch (error) {
        console.error('Error loading enquiry types:', error);
      }
    };

    loadDropdownData();
  }, []);

  const validateEmail = (email: string): boolean => {
    if (!email) return true; // if empty, validation passes (optional field)
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(email.trim());
  };

  const validateCellphone = (cellNumber: string): boolean => {
    if (!cellNumber) return true; // if empty, validation passes (optional field)
    const cleaned = cellNumber.replace(/\s+/g, ''); // remove spaces
    const pattern = /^(0(6|7|8)[0-9]{8}|27(6|7|8)[0-9]{8})$/;
    return pattern.test(cleaned);
  };

  const validationRules: ValidationRules = {
    enquiryTypeId: [{ required: true, message: 'Enquiry Type is required' }],
    name: [{ required: true, message: 'Name is required' }],
    surname: [{ required: true, message: 'Surname is required' }],
    emailAddress: [
      { required: true, message: 'Email is required' },
      { custom: (value) => validateEmail(String(value || '')), message: 'Email is not valid' }
    ],
    contact: [
      { required: true, message: 'Cellphone is required' },
      { custom: (value) => validateCellphone(String(value || '')), message: 'Cellphone is not valid' }
    ],
    contactRequest: [{ required: true, message: 'Enquiry is required' }]
  };

  const { errors, validate } = useFormValidation<EnquiryDTO>(validationRules);

  const handleSave = () => {
    if (validate(formData)) {
      onSubmit(formData);
    }
  };

  const sections = Object.values(FORM_SECTIONS);

  return (
    <Form sections={sections} onSave={handleSave} onCancel={onCancel} isSubmitting={isLoading}>
      <FormSection
        id={FORM_SECTIONS.basicInfo.id}
        title={FORM_SECTIONS.basicInfo.title}
      >
        <FormField
          id="enquiryTypeId"
          label="Enquiry Type"
          type="dropdown"
          value={formData.enquiryTypeId || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Select Enquiry Type"
          required={true}
          error={errors.enquiryTypeId}
          options={enquiryTypeOptions}
          isLoading={isLoadingEnquiryTypes}
        />
        <FormField
          id="name"
          label="Name"
          type="text"
          value={formData.name || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="e.g John"
          required={true}
          error={errors.name}
        />
        <FormField
          id="surname"
          label="Surname"
          type="text"
          value={formData.surname || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="e.g Doe"
          required={true}
          error={errors.surname}
        />
        <FormField
          id="emailAddress"
          label="Email"
          type="email"
          value={formData.emailAddress || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="e.g john@doe.com"
          textCase='lowercase'
          required={true}
          error={errors.emailAddress}
        />
        <FormField
          id="contact"
          label="Cellphone"
          type="text"
          value={formData.contact || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="e.g 0831234567"
          required={true}
          error={errors.contact}
        />
        <FormField
          id="contactRequest"
          label="Enquiry"
          type="textarea"
          value={formData.contactRequest || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Please provide your enquiry here"
          required={true}
          error={errors.contactRequest}
        />

      </FormSection>
    </Form>
  );
};

export default EnquiryForm;